// src/components/black-friday/BFOutdatedScriptAlert.tsx
import { Link } from "react-router-dom";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { AlertTriangle } from "lucide-react";
import { BFAccount } from "@/types/black-friday";

// Utility function to compare semantic versions (e.g., "1.0.9" < "1.0.10")
// Returns true if versionA is less than versionB, false otherwise.
// Handles missing versions or parts (treats missing as 0).
const compareVersions = (
  versionA: string | undefined | null,
  versionB: string,
): boolean => {
  if (!versionA) return true; // Treat null, undefined, or empty string as older

  const partsA = versionA.split(".").map(Number);
  const partsB = versionB.split(".").map(Number);

  for (let i = 0; i < Math.max(partsA.length, partsB.length); i++) {
    const partA = partsA[i] || 0;
    const partB = partsB[i] || 0;

    if (partA < partB) return true;
    if (partA > partB) return false;
  }

  return false; // Versions are equal
};

interface BFOutdatedScriptAlertProps {
  accounts: BFAccount[];
  requiredScriptVersion: string;
}

export function BFOutdatedScriptAlert({
  accounts,
  requiredScriptVersion,
}: BFOutdatedScriptAlertProps) {
  const outdatedAccounts = accounts.filter((account) =>
    compareVersions(account.scriptVersion, requiredScriptVersion),
  );

  if (outdatedAccounts.length === 0) {
    return null;
  }

  return (
    <Alert
      variant="destructive"
      className="border-red-500 bg-red-50 text-red-900 dark:bg-red-900/80 dark:border-red-500 dark:text-white shadow-md mb-4"
    >
      <AlertTriangle className="h-4 w-4 text-red-600 dark:text-red-300" />
      <AlertTitle className="text-red-800 dark:text-red-200 font-bold">
        Outdated Black Friday Script Detected
      </AlertTitle>
      <AlertDescription className="text-red-800 dark:text-red-100">
        The following account(s) are using an older Black Friday script version
        (or the script version can't be found). This may cause issues with the
        Black Friday dashboard.
        <ul className="list-disc pl-5 mt-2 font-bold text-lg">
          {outdatedAccounts.map((acc) => (
            <li key={acc.id}>
              {acc.accountName} (v{acc.scriptVersion || "Unknown"})
            </li>
          ))}
        </ul>
        <p className="mt-2">
          <strong>Easy Fix:</strong> Go to the{" "}
          <Link
            to="/bf/setup"
            className="font-medium underline text-blue-700 dark:text-blue-300"
          >
            Black Friday setup page
          </Link>{" "}
          to copy the latest script code (v{requiredScriptVersion}) and replace
          the old script in your Google Ads account. Then run the script again
          to update your Google Sheet.
        </p>
        <p className="mt-2 text-sm">
          <strong>Quick Hack: </strong>
          Open the account's Black Friday Google Sheet and find the 'dashboard'
          tab. The script version is shown in column C (row 2). If you don't
          want to update the script yet but want to clear this warning, change
          the cell value to: {requiredScriptVersion}
        </p>
      </AlertDescription>
    </Alert>
  );
}
